import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../contexts/AuthContext";
import { FONT_FAMILY_SERIF } from "../../theme";
import { trackNavigationClick } from "../../utils/analytics";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ open, onClose }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { label: "About", path: "/about" },
    { label: "Connect", path: "/contact" },
  ];

  // Add Admin link if user is authenticated
  if (isAuthenticated && user) {
    navLinks.push({ label: "Admin", path: "/admin" });
  }

  const handleLinkClick = (linkPath: string, linkLabel: string) => {
    trackNavigationClick(linkLabel, linkPath);
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-label="Navigation menu"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "400px",
          backgroundColor: "background.default",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_FAMILY_SERIF,
              fontSize: "1.5rem",
              fontWeight: 300,
              letterSpacing: "0.02em",
            }}
          >
            MB
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close navigation menu"
            sx={{
              color: "text.primary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Navigation Links */}
        <List
          sx={{
            flex: 1,
            pt: 4,
            px: 3,
          }}
        >
          {navLinks.map((link) => (
            <ListItem
              key={link.path}
              sx={{
                px: 0,
                py: 2,
              }}
            >
              <Typography
                component={Link}
                to={link.path}
                onClick={() => handleLinkClick(link.path, link.label)}
                aria-current={isActive(link.path) ? "page" : undefined}
                sx={{
                  width: "100%",
                  fontSize: "1.5rem",
                  fontWeight: isActive(link.path) ? 500 : 400,
                  color: isActive(link.path)
                    ? "text.primary"
                    : "text.secondary",
                  textDecoration: "none",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: "text.primary",
                  },
                }}
              >
                {link.label}
              </Typography>
            </ListItem>
          ))}
        </List>

        {/* User Info & Logout */}
        {isAuthenticated && user && (
          <>
            <Divider />
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Logged in as {user.email}
              </Typography>
              <Typography
                component="button"
                onClick={handleLogout}
                aria-label="Log out"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.primary",
                  textDecoration: "underline",
                  background: "none",
                  border: "none",
                  padding: 0,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                Log out
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};
